import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GetJson, PutJson, PostJson, DeleteJson } from '@awp/helpers/fetchReact';
import { REST_API } from '@awp/constants';
import PropTypes from 'prop-types';

import { FORM_MODE, Permissions } from '@awp/constants';
import * as Yup from 'yup';

import { selectAllHazardTrendDropDown, currentAnalysis, setEditing, setAnalysis } from '@stores/hazardForecastSlice';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectAvalancheTechnicians } from '@awp/stores/dropdownsSlice';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { Card, Button, Spinner, Container } from 'react-bootstrap';

import { FormTextArea, FormSelect, FormDatePicker } from '@components/Common/AwpFormInputs';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import { hasAccess } from '@components/Authorize';
import { Formik, Form } from 'formik';
import { convLocalToIsoString, getUtcToLocal } from '@awp/helpers/utilsReact';
import { toast } from 'react-toastify';

import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';

const SupportAnalysisEdit = ({ setMode, mode, eventId, setLoading, loading, fullData, setEventTypeToActOn }) => {
  const dispatch = useDispatch();
  const selectedAnalysis = useSelector(currentAnalysis);
  const hazardTrendList = useSelector(selectAllHazardTrendDropDown);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const defaultDto = {
    hazardForecastComment: '',
    concurrencyControlNumber: null,
  };

  const [dto, setDto] = useState(defaultDto);
  const [eventErrors, setEventErrors] = useState(null);

  const readOnly = mode !== FORM_MODE.EDIT && mode !== FORM_MODE.CREATE;
  const buttonLabel = 'Save';

  const title = mode === FORM_MODE.EDIT ? 'Edit Analysis' : 'Analysis';

  useEffect(() => {
    if (mode !== FORM_MODE.CREATE && selectedAnalysis) {
      setDto(entityToDto(selectedAnalysis));
    }
  }, []);

  const entityToDto = (entity) => {
    const dtoTemp = {
      hazardForecastComment: entity.HazardForecastComment ? entity.HazardForecastComment.DetailText : '',
      weatherComment: entity.WeatherComment ? entity.WeatherComment.DetailText : '',
      snowpackDescription: entity.SnowpackDescription ? entity.SnowpackDescription.DetailText : '',
      avalancheObservationSummary: entity.AvalancheObservationSummary
        ? entity.AvalancheObservationSummary.DetailText
        : '',
      avalancheActivityForecast: entity.AvalancheActivityForecast ? entity.AvalancheActivityForecast.DetailText : '',
      trend:
        hazardTrendList.find(
          (l) =>
            entity.HazardTrend &&
            l.value === entity.HazardTrend.DetailText &&
            l.eventTypePropertyId === entity.HazardTrend.EventTypePropertyId,
        ) || null,
    };
    return dtoTemp;
  };
  const getAnalysisTextObj = (obj, evtId, txt) => {
    if (!obj || !obj.EventId)
      return {
        EventId: evtId,
        DetailText: isValueEmpty(txt) ? '' : txt,
      };
    return {
      EventId: evtId,
      DetailText: isValueEmpty(txt) ? '' : txt,
      EventTypePropertyId: obj.EventTypePropertyId,
      Id: obj.Id,
      ConcurrencyControlNumber: obj.ConcurrencyControlNumber,
    };
  };
  const isAnalysisEmpty = (values) => {
    return (
      isValueEmpty(values.hazardForecastComment) &&
      isValueEmpty(values.weatherComment) &&
      isValueEmpty(values.snowpackDescription) &&
      isValueEmpty(values.avalancheObservationSummary) &&
      isValueEmpty(values.avalancheActivityForecast) &&
      (isValueEmpty(values.trend) || isValueEmpty(values.trend.value))
    );
  };
  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    if (mode === FORM_MODE.EDIT) {
      let analysisEntity = {};
      analysisEntity.EventId = selectedAnalysis.EventId;
      analysisEntity.HazardForecastComment = getAnalysisTextObj(
        selectedAnalysis.HazardForecastComment,
        selectedAnalysis.EventId,
        values.hazardForecastComment,
      );
      analysisEntity.WeatherComment = getAnalysisTextObj(
        selectedAnalysis.WeatherComment,
        selectedAnalysis.EventId,
        values.weatherComment,
      );
      analysisEntity.SnowpackDescription = getAnalysisTextObj(
        selectedAnalysis.SnowpackDescription,
        selectedAnalysis.EventId,
        values.snowpackDescription,
      );
      analysisEntity.AvalancheObservationSummary = getAnalysisTextObj(
        selectedAnalysis.AvalancheObservationSummary,
        selectedAnalysis.EventId,
        values.avalancheObservationSummary,
      );
      analysisEntity.AvalancheActivityForecast = getAnalysisTextObj(
        selectedAnalysis.AvalancheActivityForecast,
        selectedAnalysis.EventId,
        values.avalancheActivityForecast,
      );
      analysisEntity.HazardTrend = getAnalysisTextObj(
        selectedAnalysis.HazardTrend,
        selectedAnalysis.EventId,
        values.trend ? values.trend.value : '',
      );
      updateAnalysis(setSubmitting, resetForm, setErrors, analysisEntity);
    } else {
      const entity = {
        EventId: eventId,
        HazardForecastComment: {
          EventId: eventId,
          DetailText: values.hazardForecastComment ? values.hazardForecastComment : '',
        },
        WeatherComment: {
          EventId: eventId,
          DetailText: values.weatherComment ? values.weatherComment : '',
        },
        SnowpackDescription: {
          EventId: eventId,
          DetailText: values.snowpackDescription ? values.snowpackDescription : '',
        },
        AvalancheObservationSummary: {
          EventId: eventId,
          DetailText: values.avalancheObservationSummary ? values.avalancheObservationSummary : '',
        },
        AvalancheActivityForecast: {
          EventId: eventId,
          DetailText: values.avalancheActivityForecast ? values.avalancheActivityForecast : '',
        },
        HazardTrend: {
          EventId: eventId,
          DetailText: values.trend ? values.trend.value : '',
        },
      };
      createAnalysis(setSubmitting, setErrors, entity);
    }
  };
  const createAnalysis = (setSubmitting, setErrors, hzDto) => {
    const url = REST_API.HAZARD_FORECAST_ANALYSIS;
    PostJson(url, hzDto)
      .then(async (response) => {
        if (response.ok) {
          const entity = await response.json();
          const dtoTemp = entityToDto(entity);
          dispatch(setAnalysis(entity));
          setDto(dtoTemp);
          toast.success('Analysis has been created');
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEventErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const updateAnalysis = (setSubmitting, resetForm, setErrors, hzDto) => {
    setSubmitting(true);
    const url = REST_API.HAZARD_FORECAST_ANALYSIS + hzDto.EventId;
    PutJson(url, hzDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Analysis has been updated');
          const entity = await response.json();
          const dtoTemp = entityToDto(entity);
          dispatch(setAnalysis(entity));
          setDto(dtoTemp);
          setMode(FORM_MODE.VIEW);
          resetForm();
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEventErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const deleteAnalysis = () => {
    const url = REST_API.HAZARD_FORECAST_ANALYSIS + selectedAnalysis.EventId;

    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          toast.success('Analysis has been deleted');
          dispatch(
            setAnalysis(null), //clean previous cache
          );
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          if (errors.entity) setEventErrors({ Analysis: errors.entity });
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };
  const handleBtnClose = (props) => {
    props.resetForm();
    if (mode === FORM_MODE.CREATE) {
      dispatch(setAnalysis(null));
    }
    setMode(FORM_MODE.VIEW);
  };
  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };
  const Buttons = ({ props }) => {
    return mode === FORM_MODE.EDIT || mode === FORM_MODE.CREATE ? (
      <>
        <div className="mt-2 mr-3" style={{ float: 'left' }}>
          {!showDeleteConfirm && !showCancelConfirm && (
            <Button
              className="mr-2"
              variant="outline-primary"
              onClick={() => {
                setShowCancelConfirm(true);
              }}
            >
              {'Cancel'}
            </Button>
          )}

          {!showDeleteConfirm && !showCancelConfirm && hasAccess([Permissions.WriteAvalancheHazard]) && (
            <>
              <Button
                type="submit	"
                variant="primary"
                disabled={!props.isValid || !props.dirty || props.isSubmitting || isAnalysisEmpty(props.values)}
              >
                {props.isSubmitting ? (
                  <>
                    <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  <span>{buttonLabel}</span>
                )}
              </Button>
            </>
          )}
          {!showDeleteConfirm &&
            !showCancelConfirm &&
            hasAccess([Permissions.WriteAvalancheHazard]) &&
            mode === FORM_MODE.EDIT && (
              <Button
                className="ml-2"
                variant="danger"
                disabled={!props.isValid || props.isSubmitting}
                onClick={() => {
                  onClickDelete();
                }}
              >
                Delete
              </Button>
            )}
          {showCancelConfirm && (
            <AwpYesNoDialog
              action={() => handleBtnClose(props)}
              setShowYesNoConfirm={setShowCancelConfirm}
              msg={'Are you sure you want to cancel?'}
              props={{}}
            />
          )}
          {showDeleteConfirm && (
            <AwpYesNoDialog action={deleteAnalysis} setShowYesNoConfirm={setShowDeleteConfirm} props={props} />
          )}
        </div>
      </>
    ) : (
      mode === FORM_MODE.VIEW && (
        <Button
          className="mr-2"
          variant="outline-primary"
          onClick={() => {
            setShowCancelConfirm(false);
            setMode(FORM_MODE.EDIT);
          }}
        >
          {'Edit'}
        </Button>
      )
    );
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <Formik
            enableReinitialize
            validateOnMount
            validateOnBlur
            validateOnChange
            initialValues={dto}
            validationSchema={Yup.object({
              hazardForecastComment: Yup.string()
                .optional()
                .label('Hazard forecast comments')
                .max(4000, '${label} must not exceed 4000 characters'),
              weatherComment: Yup.string()
                .optional()
                .label('Weather comments')
                .max(4000, '${label} must not exceed 4000 characters'),
              snowpackDescription: Yup.string()
                .optional()
                .label('Snowpack description')
                .max(4000, '${label} must not exceed 4000 characters'),
              avalancheObservationSummary: Yup.string()
                .optional()
                .label('Avalanche observation summary')
                .max(4000, '${label} must not exceed 4000 characters'),
              avalancheActivityForecast: Yup.string()
                .optional()
                .label('Avalanche activity forecast')
                .max(4000, '${label} must not exceed 4000 characters'),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <>
                <Form>
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}
                  >
                    <Card.Body>
                      <FormTextArea
                        readOnly={readOnly}
                        showCharacterCount={true}
                        maxLength={4000}
                        label="Hazard forecast comments"
                        id="hazardForecastComment"
                        name="hazardForecastComment"
                        rows={3}
                      />
                      <FormTextArea
                        readOnly={readOnly}
                        showCharacterCount={true}
                        maxLength={4000}
                        label="Weather comments"
                        id="weatherComment"
                        name="weatherComment"
                        rows={3}
                      />
                      <FormTextArea
                        readOnly={readOnly}
                        showCharacterCount={true}
                        maxLength={4000}
                        label="Snowpack description"
                        id="snowpackDescription"
                        name="snowpackDescription"
                        rows={3}
                      />
                      <FormTextArea
                        readOnly={readOnly}
                        showCharacterCount={true}
                        maxLength={4000}
                        label="Avalanche observation summary"
                        id="avalancheObservationSummary"
                        name="avalancheObservationSummary"
                        rows={3}
                      />
                      <FormTextArea
                        readOnly={readOnly}
                        showCharacterCount={true}
                        maxLength={4000}
                        label="Avalanche activity forecast"
                        id="avalancheActivityForecast"
                        name="avalancheActivityForecast"
                        rows={3}
                      />
                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="trend"
                        id="trend"
                        label="Avalanche Hazard Trend"
                        options={hazardTrendList?.filter((l) => l.isActive)}
                        isMulti={false}
                        isClearable={true}
                      />
                      <br />
                      <Buttons title=" " props={props} />
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEventErrors} errors={eventErrors} />
        </>
      )}
    </>
  );
};

SupportAnalysisEdit.propTypes = {
  // setMode: PropTypes.func,
  // mode: PropTypes.string,
  // setEventId: PropTypes.func,
  // eventId: PropTypes.number,
  // setLoading: PropTypes.func,
  // loading: PropTypes.bool,
  // setFieldValue: PropTypes.func,
  // validateField: PropTypes.func,
  // setFieldTouched: PropTypes.func,
  // values: PropTypes.any,
  // resetForm: PropTypes.func,
  // fullData: PropTypes.array,
  // setEventTypeToActOn: PropTypes.func,
};

export default SupportAnalysisEdit;
